/**
 * <ModuleCollisionMonitor> — fires `onCollisionsChange` when the set of
 * physically overlapping track MODULES changes.
 *
 * Unlike the mover <CollisionMonitor> (which runs in `useFrame` because
 * movers move at 60 Hz through the MoverPositionStore), modules are static:
 * they only move when the config or a `trackTransform` changes. So this
 * monitor recomputes in a plain `useEffect` keyed on those inputs — no
 * per-frame loop, no Canvas dependency.
 *
 * Change detection mirrors <CollisionMonitor>: the callback fires only when
 * a stable hash key changes (pair appears / disappears, or `overlapMm`
 * shifts by more than PENETRATION_EPS_MM), so a steady scene doesn't keep
 * the callback hot across re-renders.
 */

import { useEffect, useRef } from 'react';
import { buildChain } from '../../geometry/ChainBuilder.js';
import {
  buildModuleProbes,
  checkModuleCollisions,
  type CheckModuleCollisionsOptions,
  type ModuleCollision,
  type ModuleCollisionPartInput,
  type ModuleCollisionXpuInput,
} from '../../geometry/moduleCollision.js';
import type { NormalizedXtsConfig } from '../../geometry/normalizeXtsConfig.js';
import type { Orientation, TrackTransform } from '../../types.js';

const PENETRATION_EPS_MM = 0.01;

interface ModuleCollisionMonitorProps {
  config: NormalizedXtsConfig;
  orientation?: Orientation;
  trackTransformOverrides?: Record<number, TrackTransform>;
  warningGapMm?: number;
  onCollisionsChange: (collisions: ModuleCollision[]) => void;
}

export const ModuleCollisionMonitor: React.FC<ModuleCollisionMonitorProps> = ({
  config,
  orientation,
  trackTransformOverrides,
  warningGapMm,
  onCollisionsChange,
}) => {
  const lastKeyRef = useRef<string>('');

  useEffect(() => {
    const xpus: ModuleCollisionXpuInput[] = [];
    for (const xpu of config.processingUnits) {
      const effective =
        trackTransformOverrides?.[xpu.objectId] ?? xpu.trackTransform;
      const parts: ModuleCollisionPartInput[] = [];
      for (const part of xpu.parts) {
        try {
          parts.push({
            partObjectId: part.objectId,
            partKey: part.globalNumber,
            ...(part.partTransformation
              ? { partTransformation: part.partTransformation }
              : {}),
            chain: buildChain(part.modules),
          });
        } catch {
          // already validated by normalizeXtsConfig — defensive.
        }
      }
      xpus.push({
        xpuObjectId: xpu.objectId,
        ...(effective ? { trackTransform: effective } : {}),
        parts,
      });
    }

    const opts: CheckModuleCollisionsOptions = {};
    if (warningGapMm !== undefined) opts.warningGapMm = warningGapMm;

    const { probes, adjacency } = buildModuleProbes(orientation, xpus, opts);
    const all = checkModuleCollisions(probes, adjacency, opts);

    // Stable key — only fire when the meaningful set changes. Floor
    // overlapMm so float-math jitter doesn't keep the callback hot.
    const key = all
      .map((c) => {
        const floor =
          Math.round(c.overlapMm / PENETRATION_EPS_MM) * PENETRATION_EPS_MM;
        return `${c.a.processingUnitObjectId}.${c.a.partObjectId}.${c.a.moduleIndex}|${c.b.processingUnitObjectId}.${c.b.partObjectId}.${c.b.moduleIndex}|${floor.toFixed(2)}`;
      })
      .join(';');

    if (key !== lastKeyRef.current) {
      lastKeyRef.current = key;
      onCollisionsChange(all);
    }
    // `onCollisionsChange` intentionally omitted from deps — the host may
    // pass an inline callback; we only want to recompute on data changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, orientation, trackTransformOverrides, warningGapMm]);

  return null;
};
